/* eslint semi: 1 */
import { computed, inject } from 'vue';
import { DateTime } from 'luxon';
import { tagsStore } from '@/pinia/stores/tagsStore';
import {
  useMediaQueryStore,
  type MediaOrientationDir,
  type MediaFileTypes,
  type MediaSortOrder,
  type MediaExtendedSortOrder,
  type MediaPageContextValue
} from '@/pinia/stores/media/mediaQueryStore';
import {
  useLocalStorageStore,
  RESOLUTIONS_CONTEXT_FILES,
  RESOLUTIONS_CONTEXT_LED,
  RESOLUTIONS_CONTEXT_VIDEOWALL,
  RESOLUTIONS_CONTEXT_ALERTS
} from '@base-shared/stores/local-storage';
import { useFuseWorker } from '@base-shared/composables/use-fuse-worker';
import CONST from '@/globals/constants';
import type FiltersRepository from '@/globals/filtersRepository';
import type Utils from '@/globals/utils';
import type { operations } from '@base-shared/types/media.openapi';
import type { MediaResourceItem } from '@/pinia/stores/media/mediaStore';

type ListMediaQuery = operations['listMedia']['parameters']['query'];
export type LEDTagDimension = { width: number, height: number, name: string, dimension: string, value: string, queryStringValue: string };

const DEFAULT_LED_TAG = {
  width: 1080,
  height: 1080,
  name: '1080x1080',
  dimension: '1080x1080',
  value: '',
  queryStringValue: ''
};

/**
 * Composable for building and managing the `GET /media` API query.
 *
 * Bridges `mediaQueryStore` (Pinia reactive state) with `$filtersRepository`
 * (URL query-param + localStorage persistence layer) so that filter state
 * survives page refreshes and is reflected in the URL.
 *
 * Page-context flags (`isLed`, `isVideowall`, `isAlert`) live in the store and
 * are set once by the host page via `setPageContext()`. This allows every
 * composable that calls `useMediaQuery()` (e.g. `useMediaFetching`,
 * `useMediaFiltering`, `useMediaSearching`) to share the same context without
 * constructor-arg threading.
 */
export function useMediaQuery () {
  const $filtersRepository = inject('$filtersRepository') as typeof FiltersRepository;
  const $utils = inject('$utils') as typeof Utils;
  const store = useMediaQueryStore();
  const tagStore = tagsStore();
  const localStorageStore = useLocalStorageStore();

  /** Instantiate FuseWorker for async fuzzy search */
  const { search: fuseWorkerSearch } = useFuseWorker();

  /** Reactive states (read-only computed refs) */
  const pageContext = computed(() => store.mediaPageContext);
  const isFilesPage = computed(() => store.isFiles);
  const isLedPage = computed(() => store.isLed);
  const isVideowallPage = computed(() => store.isVideowall);
  const isAlertPage = computed(() => store.isAlert);

  const orientation = computed(() => store.orientation);
  const searchText = computed(() => store.searchText);
  const mediaTypes = computed(() => store.mediaTypes);
  const isScheduled = computed(() => store.isScheduled);
  const mediaTags = computed(() => store.mediaTags);
  const popIds = computed(() => store.popIds);
  const ledTags = computed(() => store.ledTags);

  const modifiedAtFrom = computed(() => store.modifiedAtFrom);
  const modifiedAtTo = computed(() => store.modifiedAtTo);
  const sortOrder = computed(() => store.sortOrder);

  const isOrientationVertical = computed(() => store.isOrientationVertical);
  const hasActiveFilters = computed(() => store.hasActiveFilters);
  const hasActiveQuery = computed(() => store.hasActiveQuery);
  const isDefaultSort = computed(() => sortOrder.value === CONST.SORT_ORDER_DEFAULT);

  /**
   * Parses the first active LED tag into a structured resolution object.
   * Falls back to a 1080×1080 default when no tag is selected.
   */
  const selectedLedTag = computed(() => {
    if (!store.ledTags.length) return DEFAULT_LED_TAG;

    let tag = store.ledTags[0];
    if (!tag.startsWith('LED:')) tag = `LED:${tag}`;
    return $utils.parseLedTag(tag);
  });

  /**
   * Builds the complete API request-params object from current query state.
   */
  const apiParams = computed(() => {
    const params: ListMediaQuery = {};

    // Direction / orientation — omitted for LED and Videowall pages
    if (!store.isLed && !store.isVideowall) {
      params.dir = store.orientation;
    }

    // Free-text search
    if (store.searchText) {
      params.search = store.searchText;
    }

    // Media type filter (comma-separated, never includes 'scheduled')
    if (store.mediaTypes.length) {
      params.type = store.mediaTypes.join(',');
    }

    // Scheduled filter — clean boolean, no longer embedded in mediaTypes
    if (store.isScheduled !== null && store.isScheduled !== undefined) {
      params.scheduled = store.isScheduled;
    }

    // Resource tag filter
    if (store.mediaTags.length) {
      params.tags = JSON.stringify(store.mediaTags);
    }

    // PoP ID filter — excluded for alert pages
    if (store.popIds.length && !store.isAlert) {
      params.popIds = JSON.stringify(store.popIds);
    }

    // LED dimension — used for both LED and Videowall pages
    if (store.isLed || store.isVideowall) {
      params.led = selectedLedTag.value.dimension;
    }

    // Modified-date range filter - used for filtering with dates
    if (store.modifiedAtFrom) {
      const fromDate = DateTime.fromFormat(store.modifiedAtFrom, 'yyyy-MM-dd').startOf('day');
      if (fromDate.isValid) {
        params.modifiedAtFrom = fromDate.toUTC().toISO();
      }
    }
    if (store.modifiedAtTo) {
      const toDate = DateTime.fromFormat(store.modifiedAtTo, 'yyyy-MM-dd').endOf('day');
      if (toDate.isValid) {
        params.modifiedAtTo = toDate.toUTC().toISO();
      }
    }

    // Sort order — omit when the API default is in effect to keep URLs clean
    // Only 'asc' value will be sent to the API, since 'desc' is the default.
    if (store.sortOrder && store.sortOrder === CONST.MEDIA_SORT_ASC) {
      params.sortOrder = store.sortOrder;
    }

    return params;
  });

  function applyOrientation (value: MediaOrientationDir) {
    store.setOrientation(value);
    $filtersRepository.saveOrientation(value);
    // Persist the last-used orientation for this Files page
    // or Alerts page context in localStorage
    localStorageStore.setAryaMediaResolutionForContext(
      isFilesPage.value ? RESOLUTIONS_CONTEXT_FILES : RESOLUTIONS_CONTEXT_ALERTS,
      value ?? null
    );
  }

  function applySearchText (value: string) {
    // Strip ASCII control characters (null, CR, LF, etc.) that are never valid
    // in a search term. All other special characters (e.g. +, &, #, =) are kept
    // as-is — Vue Router and axios encode them automatically when serialising
    // query params / request params, so there is no need to encode here and
    // double-encoding would corrupt the value.
    const safe = (value ?? '')
      .replace(/[\0-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
      .slice(0, 255);
    store.setSearchText(safe);
    if (safe) {
      $filtersRepository.saveSearchText(safe);
    } else {
      $filtersRepository.deleteSearchText();
    }
  }

  /**
   * @param {MediaFileTypes[]} types — must NOT include `'scheduled'`; use `applyIsScheduled` for that.
   */
  function applyMediaTypes (types: MediaFileTypes[]) {
    store.setMediaTypes(types);
    if (types.length) {
      $filtersRepository.saveMediaTypes(types);
    } else {
      $filtersRepository.deleteMediaTypes();
    }
  }

  /**
   * @param {boolean | null} value
   *   `true`  = scheduled only,
   *   `false` = unscheduled only,
   *   `null`  = no filter
   */
  function applyIsScheduled (value: boolean | null) {
    store.setIsScheduled(value);
    if (typeof value === 'boolean') {
      $filtersRepository.saveScheduled(value);
    } else {
      $filtersRepository.deleteScheduled();
    }
  }

  /**
   * Sets the modified-date range filter and persists both keys.
   * Pass `null` for either boundary to clear just that end of the range.
   */
  function applyModifiedAtRange (dateRange: { from: string | null, to: string | null }) {
    const { from, to } = dateRange;
    store.setModifiedAtFrom(from ?? null);
    store.setModifiedAtTo(to ?? null);
    if (from) {
      $filtersRepository.saveModifiedAtFrom(from);
    } else {
      $filtersRepository.deleteModifiedAtFrom();
    }
    if (to) {
      $filtersRepository.saveModifiedAtTo(to);
    } else {
      $filtersRepository.deleteModifiedAtTo();
    }
  }

  /**
   * Sets the sort order and persists to the repository.
   * Removes the URL key when reverting to the API default to keep URLs clean.
   */
  function applySortOrder (value: MediaExtendedSortOrder) {
    store.setSortOrder(value ?? CONST.SORT_ORDER_DEFAULT);
    if (value && value !== CONST.SORT_ORDER_DEFAULT) {
      $filtersRepository.saveSortOrder(value);
    } else {
      $filtersRepository.deleteSortOrder();
    }
  }

  /**
   * Applies all filter-panel params (mediaTypes, isScheduled, modifiedAtRange)
   * in a single URL update.
   *
   * Do NOT replace this with individual `applyMediaTypes()` + `applyIsScheduled()`
   * + `applyModifiedAtRange()` calls in the same tick — each of those calls
   * `updateWindowLocationQueryParams` independently, and because `router.push` is
   * async the URL hasn't been updated by the time the next call reads it. The
   * residuals-preservation loop then re-adds keys that were just removed.
   */
  function applyFilterPanelParams (params: {
    types: MediaFileTypes[],
    scheduled: boolean | null,
    modifiedAtFrom: string | null,
    modifiedAtTo: string | null
  }) {
    const { types, scheduled, modifiedAtFrom, modifiedAtTo } = params;
    store.setMediaTypes(types);
    store.setIsScheduled(scheduled);
    store.setModifiedAtFrom(modifiedAtFrom);
    store.setModifiedAtTo(modifiedAtTo);
    $filtersRepository.setKeys([
      { key: CONST.FILTER_KEY_MEDIA_TYPES, values: types.length ? types : null },
      { key: CONST.FILTER_KEY_SCHEDULED, values: (scheduled !== null && scheduled !== undefined) ? [String(scheduled)] : null },
      { key: CONST.FILTER_KEY_MODIFIED_AT_FROM, values: modifiedAtFrom ? [modifiedAtFrom] : null },
      { key: CONST.FILTER_KEY_MODIFIED_AT_TO, values: modifiedAtTo ? [modifiedAtTo] : null }
    ]);
  }

  function applyMediaTags (tags: string[]) {
    store.setMediaTags(tags);
    $filtersRepository.saveMediaTags(tags);
  }

  function applyPopIds (ids: string[]) {
    store.setPopIds(ids);
    $filtersRepository.savePopIds(ids);
  }

  function applyLedTags (tags: string[]) {
    store.setLedTags(tags);
    if (store.isVideowall) {
      $filtersRepository.saveVideowallTags(tags);
    } else {
      $filtersRepository.saveLedTags(tags);
    }

    // Persist the last-used LED/Videowall tag for this page
    // context in localStorage
    localStorageStore.setAryaMediaResolutionForContext(
      store.isVideowall ? RESOLUTIONS_CONTEXT_VIDEOWALL : RESOLUTIONS_CONTEXT_LED,
      tags[0] ?? null
    );
  }

  function clearSearchText () {
    store.setSearchText('');
    $filtersRepository.deleteSearchText();
  }

  function clearMediaTypes () {
    store.setMediaTypes([]);
    $filtersRepository.deleteMediaTypes();
  }

  function clearIsScheduled () {
    store.setIsScheduled(null);
    $filtersRepository.deleteScheduled();
  }

  function clearModifiedAtRange () {
    store.setModifiedAtFrom(null);
    store.setModifiedAtTo(null);
    $filtersRepository.deleteModifiedAtFrom();
    $filtersRepository.deleteModifiedAtTo();
  }

  function clearSortOrder () {
    store.setSortOrder(CONST.SORT_ORDER_DEFAULT);
    $filtersRepository.deleteSortOrder();
  }

  /**
   * Clears all filter-panel managed params (mediaTypes, isScheduled, modifiedAtRange)
   * in a single URL update.
   *
   * Do NOT replace this with individual `clearMediaTypes()` + `clearIsScheduled()` +
   * `clearModifiedAtRange()` calls in the same tick — each of those calls
   * `updateWindowLocationQueryParams` independently, and because `router.push` is
   * async, the URL hasn't been updated by the time the next call reads it. The
   * residuals-preservation loop then re-adds keys that were already removed.
   */
  function clearFilterPanelParams () {
    store.setMediaTypes([]);
    store.setIsScheduled(null);
    store.setModifiedAtFrom(null);
    store.setModifiedAtTo(null);
    $filtersRepository.deleteKeys([
      CONST.FILTER_KEY_MEDIA_TYPES,
      CONST.FILTER_KEY_SCHEDULED,
      CONST.FILTER_KEY_MODIFIED_AT_FROM,
      CONST.FILTER_KEY_MODIFIED_AT_TO
    ]);
  }

  function clearMediaTags () {
    store.setMediaTags([]);
    $filtersRepository.deleteMediaTags();
  }

  function clearPopIds () {
    store.setPopIds([]);
    $filtersRepository.deletePopIds();
  }

  function clearLedTags () {
    store.setLedTags([]);
    if (store.isVideowall) {
      $filtersRepository.deleteVideowallTags();
    } else {
      $filtersRepository.deleteLedTags();
    }
  }

  /**
   * For LED/Videowall pages, this handles the selection of
   * the first available LED/Videowall tag when either:
   *   (a) nothing is persisted yet, or
   *   (b) the persisted tag is no longer in the available list (stale).
   *
   * Must be called **after** `initialize()` so that `store.ledTags`
   * already reflects the `$filtersRepository` value.
   *
   * Updates both the store and `$filtersRepository` in sync
   * when a default needs to be applied, so the new selection is
   * immediately reflected in the URL.
   */
  function applyDefaultLedTagIfNeeded () {
    // No-op for non-LED/Videowall pages
    if (!store.isLed && !store.isVideowall) return;
    // For LED/Videowall pages, pass the currently available tag options so that
    // applyDefaultLedTagIfNeeded can select the first valid tag when nothing is
    // persisted (cold start) or the stored value is stale (e.g. tag was deleted).
    const availableTags =
      store.isLed
        ? tagStore.sortedLedTags
        : store.isVideowall
          ? tagStore.sortedVideowallTags
          : [];

    if (!availableTags?.length) return;

    const currentTags = store.ledTags; // set by initialize() from $filtersRepository
    const isValidSelection = currentTags.length > 0 &&
      availableTags.some(t => currentTags.includes(t.queryStringValue));

    if (!isValidSelection) {
      applyLedTags([availableTags[0].queryStringValue]);
    }
  }

  /**
   * Sets the page-type context that drives LED/Videowall/Alert-specific behaviour
   * (which API params are emitted, which repository keys are used).
   *
   * Call this once in the host page component's `<script setup>`, **before**
   * `initialize()` is triggered. Because the context lives in the shared Pinia store,
   * every composable that calls `useMediaQuery()` internally (e.g. `useMediaFetching`,
   * `useMediaFiltering`) automatically sees the correct context.
   */
  function setPageContext (value: MediaPageContextValue = CONST.MEDIA_COMPONENT_AS_MEDIA_FILES) {
    store.reset();
    store.setPageContext(value);
  }

  /**
   * Resolves the initial `orientation` (Files/Alert pages) or `ledTags`
   * (LED/Videowall pages) from two sources, in priority order:
   *
   * 1. **URL param** (`$filtersRepository`) — set when the user navigated with
   *    an explicit query string; always wins.
   * 2. **localStorage preference** — the last value the user selected on this
   *    page type; promoted to the URL so subsequent saves stay consistent.
   *
   * When neither source has a value, returns `null` / `[]` and lets `initialize()`
   * apply the default (e.g. horizontal orientation).
   *
   * Side-effect: writes `fromPreference` back to `$filtersRepository` when the
   * URL param is absent, keeping the URL and localStorage in sync.
   */
  function initializeResolutionsFromStorage (): { orientation?: MediaOrientationDir | null, ledTags?: string[] } {
    // For Files/Alert pages, get the Orientation.
    if (store.isFiles || store.isAlert) {
      const fromURL = $filtersRepository.findOrientation();
      if (fromURL) return { orientation: fromURL };

      const ctx = store.isFiles ? RESOLUTIONS_CONTEXT_FILES : RESOLUTIONS_CONTEXT_ALERTS;
      const fromPreference = localStorageStore.getAryaMediaResolutionForContext(ctx) as MediaOrientationDir | null;
      if (fromPreference) $filtersRepository.saveOrientation(fromPreference);
      return { orientation: fromPreference ?? null };
    }

    // For LED/Videowall pages, get the LED tag(s).
    if (store.isLed || store.isVideowall) {
      const fromURL = store.isLed ? $filtersRepository.findLedTags() : $filtersRepository.findVideowallTags();
      if (fromURL?.length) return { ledTags: fromURL };

      const ctx = store.isLed ? RESOLUTIONS_CONTEXT_LED : RESOLUTIONS_CONTEXT_VIDEOWALL;
      const pref = localStorageStore.getAryaMediaResolutionForContext(ctx);
      const fromPreference = pref ? [pref] : [];
      if (fromPreference.length) {
        store.isVideowall ? $filtersRepository.saveVideowallTags(fromPreference) : $filtersRepository.saveLedTags(fromPreference);
      }
      return { ledTags: fromPreference };
    }

    return { orientation: null, ledTags: [] as string[] };
  }

  /**
   * Populates the query store from `$filtersRepository` (URL params + localStorage).
   * Call this when the page mounts or the active page context changes.
   */
  function initialize () {
    // Legacy compatibility: old URLs may still have `'scheduled'` embedded inside
    // the `mediaTypes` array. When detected it is split out into `isScheduled`
    // and the dedicated `scheduled` key is written back so subsequent saves use
    // the clean format. The legacy `mediaTypes` entry is also cleaned up.
    const rawMediaTypes = $filtersRepository.findMediaTypes();
    const legacyHasScheduled = rawMediaTypes.includes(CONST.MEDIA_TYPE_VALUE_SCHEDULED);
    const cleanMediaTypes = rawMediaTypes.filter((t: string) => t !== CONST.MEDIA_TYPE_VALUE_SCHEDULED);

    // Dedicated key takes precedence; fall back to legacy embedded value.
    let scheduled = $filtersRepository.findScheduled();
    if (scheduled === null && legacyHasScheduled) {
      // Migrate: write the dedicated key and clean up the legacy mediaTypes entry.
      scheduled = true;
      $filtersRepository.saveScheduled(true, false);
      if (cleanMediaTypes.length) {
        $filtersRepository.saveMediaTypes(cleanMediaTypes, false);
      } else {
        $filtersRepository.deleteMediaTypes();
      }
    }

    // Get orientation and ledTags, prioritize value from
    // $filtersRepository (url query) or localStorageStore
    // (user preference).
    const { orientation, ledTags } = initializeResolutionsFromStorage();

    store.setPatch({
      orientation: orientation ?? CONST.ORIENTATION_VALUE_HORIZONTAL,
      ledTags: ledTags ?? [],
      searchText: $filtersRepository.findSearchText(),
      mediaTypes: cleanMediaTypes,
      isScheduled: scheduled,
      mediaTags: $filtersRepository.findMediaTags(),
      popIds: $filtersRepository.findPopIds(),
      modifiedAtFrom: $filtersRepository.findModifiedAtFrom(),
      modifiedAtTo: $filtersRepository.findModifiedAtTo(),
      sortOrder: ($filtersRepository.findSortOrder() as MediaSortOrder) ?? CONST.SORT_ORDER_DEFAULT,
    });

    // For LED/Videowall pages, ensure a valid LED tag is
    // selected on initialization.
    applyDefaultLedTagIfNeeded();
  }

  /**
   * Resets all query params and flushes the repository
   * (clears all URL params and localStorage entries).
   */
  function clearAll () {
    store.reset();
    $filtersRepository.deleteAll(true);
  }

  function matchMediaType (item: MediaResourceItem): boolean {
    if (!store.mediaTypes.length) return true;
    if (!item.type) return false;
    return store.mediaTypes.includes(item.type as MediaFileTypes);
  }

  function matchMediaTags (item: MediaResourceItem): boolean {
    if (!store.mediaTags.length) return true;
    if (!item.tags?.length) return false;
    return store.mediaTags.every(tag => item.tags?.includes(tag));
  }

  function matchPopIds (item: MediaResourceItem): boolean {
    if (!store.popIds.length) return true;
    if (!item.popId) return false;
    return store.popIds.includes(item.popId);
  }

  function matchModifiedAt (item: MediaResourceItem): boolean {
    if (!store.modifiedAtFrom || !store.modifiedAtTo) return true;
    if (!item.modifiedAt) return false;
    const fromDate = DateTime.fromFormat(store.modifiedAtFrom, 'yyyy-MM-dd').startOf('day');
    const toDate = DateTime.fromFormat(store.modifiedAtTo, 'yyyy-MM-dd').endOf('day');
    const modifiedAt = DateTime.fromISO(item.modifiedAt);
    if (!fromDate.isValid || !toDate.isValid || !modifiedAt.isValid) return false;
    return modifiedAt >= fromDate && modifiedAt <= toDate;
  }

  /**
   * Client-side filtering for media items based on the current query state.
   */
  function filterItem (item: MediaResourceItem): boolean {
    return matchMediaType(item) &&
      matchMediaTags(item) &&
      matchPopIds(item) &&
      matchModifiedAt(item);
  }

  /**
   * Client-side sorting for media items based on the current `sortOrder` state.
   */
  function sortItem (itemA: MediaResourceItem, itemB: MediaResourceItem): number {
    if (store.sortOrder === CONST.MEDIA_SORT_ASC) {
      // Ascending by modified date (oldest first)
      return new Date(itemA.modifiedAt ?? 0).getTime() - new Date(itemB.modifiedAt ?? 0).getTime();
    } else if (store.sortOrder === CONST.MEDIA_SORT_NAME_ASC) {
      // Ascending by name (A-Z)
      return (itemA.name ?? '').localeCompare(itemB.name ?? '');
    } else if (store.sortOrder === CONST.MEDIA_SORT_NAME_DESC) {
      // Descending by name (Z-A)
      return (itemB.name ?? '').localeCompare(itemA.name ?? '');
    }

    // Default: Descending by modified date (newest first)
    return new Date(itemB.modifiedAt ?? 0).getTime() - new Date(itemA.modifiedAt ?? 0).getTime();
  }

  /**
   * Filters and sorts a list of media items based on the current query state.
   * This is used for client-side filtering of already-fetched media items.
   * @param mediaItems The list of media items to filter and sort.
   * @param options Optional parameters for filtering, such as `filterByIds`.
   * @returns The filtered and sorted list of media items.
   */
  async function filterAndSortList (
    mediaItems: MediaResourceItem[],
    options?: { filterByIds?: string[] }
  ): Promise<MediaResourceItem[]> {
    if (!mediaItems?.length) return [];

    // Apply all active filters and sorting to the provided list of media items.
    const filterByIdsSet = options?.filterByIds ? new Set(options.filterByIds) : null;
    const results = mediaItems
      .filter(item => {
        // If `filterByIds` is provided, only include items whose `resourceId` is in that list.
        if (filterByIdsSet && !filterByIdsSet.has(item.resourceId)) {
          return false;
        }

        // Apply the standard filter checks (media type, tags, PoP IDs, modified date).
        return filterItem(item);
      })
      .sort(sortItem);

    // Apply search via FuseWorker on the already filtered list.
    // Falls back to synchronous Fuse automatically when Web
    // Workers are unavailable.
    if (store.searchText) {
      return fuseWorkerSearch(results, ['name', 'tags'], store.searchText);
    }

    return results;
  }

  return {
    // Computed states (read-only)
    pageContext,
    isFilesPage,
    isLedPage,
    isVideowallPage,
    isAlertPage,
    orientation,
    searchText,
    mediaTypes,
    isScheduled,
    modifiedAtFrom,
    modifiedAtTo,
    sortOrder,
    mediaTags,
    popIds,
    ledTags,

    // Derived computed states
    isOrientationVertical,
    hasActiveFilters,
    hasActiveQuery,
    selectedLedTag,
    apiParams,
    isDefaultSort,

    // Methods
    initialize,
    setPageContext,
    // Mutations + persistence (updates store and repository in sync)
    applyOrientation,
    applySearchText,
    applyMediaTypes,
    applyIsScheduled,
    applyModifiedAtRange,
    applySortOrder,
    applyFilterPanelParams,
    applyMediaTags,
    applyPopIds,
    applyLedTags,
    applyDefaultLedTagIfNeeded,
    clearSearchText,
    clearMediaTypes,
    clearIsScheduled,
    clearModifiedAtRange,
    clearSortOrder,
    clearFilterPanelParams,
    clearMediaTags,
    clearPopIds,
    clearLedTags,
    clearAll,

    filterAndSortList
  };
}
